import React, { useState, useMemo } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { PieChartFill } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { handleClaimDashboardFilter } from "../config/claimDashboardSlice";

const PAYER_COLORS = [
  "#2563EB", // Bupa - Blue
  "#0D9488", // Tawuniya - Teal
  "#7C3AED", // MedGulf - Purple
  "#EA580C", // GIG - Orange
  "#DB2777", // Malath - Pink
  "#16A34A", // Al Rajhi Takaful - Green
  "#CA8A04", // Allianz SF - Yellow/Gold
  "#0EA5E9", // AXA Cooperative - Sky Blue
  "#DC2626", // Walaa - Red
  "#64748B", // Arabian Shield - Slate
];

const DEFAULT_PAYER_DATA = [
  { name: "Bupa", claims: 4520, gross: 1450200, color: PAYER_COLORS[0] },
  { name: "Tawuniya", claims: 3980, gross: 1250400, color: PAYER_COLORS[1] },
  { name: "MedGulf", claims: 3210, gross: 980100, color: PAYER_COLORS[2] },
  { name: "GIG", claims: 2840, gross: 850300, color: PAYER_COLORS[3] },
  { name: "Malath", claims: 2410, gross: 710200, color: PAYER_COLORS[4] },
  {
    name: "Al Rajhi Takaful",
    claims: 2150,
    gross: 640500,
    color: PAYER_COLORS[5],
  },
  { name: "Allianz SF", claims: 1890, gross: 560800, color: PAYER_COLORS[6] },
  {
    name: "AXA Cooperative",
    claims: 1650,
    gross: 490200,
    color: PAYER_COLORS[7],
  },
  { name: "Walaa", claims: 1420, gross: 420100, color: PAYER_COLORS[8] },
  {
    name: "Arabian Shield",
    claims: 1150,
    gross: 350000,
    color: PAYER_COLORS[9],
  },
];

const fmtSAR = (n, compact = false) => {
  n = Number(n) || 0;
  if (compact) {
    if (Math.abs(n) >= 1000000) return `SAR ${(n / 1000000).toFixed(2)}M`;
    if (Math.abs(n) >= 1000) return `SAR ${(n / 1000).toFixed(1)}K`;
    return `SAR ${n.toFixed(0)}`;
  }
  return `SAR ${Math.round(n).toLocaleString("en-US")}`;
};

const fmtNum = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");

const PayerMarketShareChart = ({
  data = DEFAULT_PAYER_DATA,
  onSelectPayer,
  activePayer = "All",
}) => {
  const dispatch = useDispatch();
  const reduxActivePayer = useSelector(
    (state) => state?.claimDashboard?.claimDashboardFilters?.insurance || "All",
  );
  const [internalActivePayer, setInternalActivePayer] = useState("All");
  const [metric, setMetric] = useState("gross"); // "gross" = by submitted amount, "claims" = by claim volume
  const [hoveredPayerName, setHoveredPayerName] = useState(null);

  const currentActivePayer =
    activePayer !== "All"
      ? activePayer
      : reduxActivePayer !== "All"
        ? reduxActivePayer
        : internalActivePayer;

  const sorted = useMemo(() => {
    const total = data.reduce((s, p) => s + (Number(p[metric]) || 0), 0) || 1;
    return [...data]
      .map((p, i) => ({
        ...p,
        color: p.color || PAYER_COLORS[i % PAYER_COLORS.length],
        pct: +(((Number(p[metric]) || 0) / total) * 100).toFixed(1),
      }))
      .sort((a, b) => (Number(b[metric]) || 0) - (Number(a[metric]) || 0));
  }, [data, metric]);

  const leader = sorted[0];

  const displayPayer = useMemo(() => {
    if (hoveredPayerName) {
      const found = sorted.find((p) => p.name === hoveredPayerName);
      if (found) return found;
    }
    return leader;
  }, [hoveredPayerName, sorted, leader]);

  const handleSelect = (name) => {
    const nextPayer = currentActivePayer === name ? "All" : name;
    setInternalActivePayer(nextPayer);
    if (onSelectPayer) {
      onSelectPayer(nextPayer);
    }
    dispatch(
      handleClaimDashboardFilter({
        check: "insurance",
        value: nextPayer === "All" ? null : nextPayer,
      }),
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div
        className="p-3 rounded-3 shadow-lg text-white"
        style={{
          backgroundColor: "#0F172A",
          border: "1px solid #334155",
          fontSize: "12px",
          minWidth: "190px",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.5)",
          zIndex: 9999,
        }}
      >
        <div
          className="fw-bold pb-1 mb-2 border-bottom"
          style={{ color: d.color || "#60A5FA", borderColor: "#334155" }}
        >
          {d.name}
        </div>
        <div className="d-flex justify-content-between gap-3 mb-1">
          <span style={{ color: "#94A3B8" }}>Market Share:</span>
          <span className="fw-bold" style={{ color: "#ffffff" }}>
            {d.pct}%
          </span>
        </div>
        <div className="d-flex justify-content-between gap-3 mb-1">
          <span style={{ color: "#94A3B8" }}>Submitted:</span>
          <span
            className="fw-semi-bold font-monospace"
            style={{ color: "#E2E8F0" }}
          >
            {fmtSAR(d.gross, true)}
          </span>
        </div>
        <div className="d-flex justify-content-between gap-3">
          <span style={{ color: "#94A3B8" }}>Claims:</span>
          <span
            className="fw-semi-bold font-monospace"
            style={{ color: "#E2E8F0" }}
          >
            {fmtNum(d.claims)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <Col xs={12} className="mt-3">
      <Card className="shadow-sm border">
        {/* Header Section */}
        <Card.Header className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between p-3 border-bottom gap-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "8px",
                backgroundColor: "rgba(59, 130, 246, 0.12)",
                color: "#3B82F6",
                border: "1px solid rgba(59, 130, 246, 0.25)",
              }}
            >
              <PieChartFill size={15} />
            </div>
            <div>
              <h6
                className="mb-0 fw-bold"
                style={{ fontSize: "15px" }}
              >
                Payer Market Share
              </h6>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                Who your business runs on — share of{" "}
                {metric === "gross" ? "submitted amount" : "claim volume"} by
                insurance company
              </div>
            </div>
          </div>

          {/* Toggle By Amount vs By Claims */}
          <div
            className="d-flex align-items-center gap-1 p-1 rounded"
            style={{ backgroundColor: "rgba(148, 163, 184, 0.15)" }}
          >
            <button
              type="button"
              onClick={() => setMetric("gross")}
              className="btn btn-sm border-0 fw-semi-bold px-3 py-1"
              style={{
                fontSize: "12px",
                backgroundColor: metric === "gross" ? "rgba(255, 255, 255, 0.25)" : "transparent",
                color: metric === "gross" ? "#3B82F6" : "#64748B",
                boxShadow:
                  metric === "gross" ? "0 1px 2px rgba(0, 0, 0, 0.08)" : "none",
              }}
            >
              By Amount
            </button>
            <button
              type="button"
              onClick={() => setMetric("claims")}
              className="btn btn-sm border-0 fw-semi-bold px-3 py-1"
              style={{
                fontSize: "12px",
                backgroundColor:
                  metric === "claims" ? "rgba(255, 255, 255, 0.25)" : "transparent",
                color: metric === "claims" ? "#3B82F6" : "#64748B",
                boxShadow:
                  metric === "claims"
                    ? "0 1px 2px rgba(0, 0, 0, 0.08)"
                    : "none",
              }}
            >
              By Claims
            </button>
          </div>
        </Card.Header>

        <Card.Body className="p-4">
          <Row className="align-items-center g-4">
            {/* Donut Chart with Center Leader Info */}
            <Col
              xs={12}
              lg={5}
              className="position-relative d-flex align-items-center justify-content-center"
            >
              {/* Center Leader Overlay - Renders below tooltip in DOM order with zIndex: 1 */}
              <div
                className="position-absolute top-50 start-50 translate-middle text-center px-2 d-flex flex-column align-items-center justify-content-center"
                style={{
                  width: "145px",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              >
                <div
                  className="fw-bold text-muted text-uppercase"
                  style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                >
                  {hoveredPayerName ? "" : "Top Payer"}
                </div>
                <div
                  className="fw-bold text-truncate my-1"
                  style={{
                    fontSize: "18px",
                    lineHeight: "1.2",
                    maxWidth: "140px",
                  }}
                  title={displayPayer?.name}
                >
                  {displayPayer?.name}
                </div>
                <div
                  className="fw-bold"
                  style={{
                    fontSize: "14px",
                    color: displayPayer?.color || "#2563EB",
                  }}
                >
                  {displayPayer?.pct}% share
                </div>
                <div
                  className="text-muted font-monospace mt-1"
                  style={{ fontSize: "11px" }}
                >
                  {metric === "gross"
                    ? fmtSAR(displayPayer?.gross, true)
                    : `${fmtNum(displayPayer?.claims)} clm`}
                </div>
              </div>

              {/* PieChart Container - Renders above center overlay in DOM order with zIndex: 10 */}
              <div
                style={{
                  height: 280,
                  width: "100%",
                  position: "relative",
                  zIndex: 10,
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart onMouseLeave={() => setHoveredPayerName(null)}>
                    <Pie
                      data={sorted}
                      dataKey={metric}
                      nameKey="name"
                      innerRadius={74}
                      outerRadius={106}
                      paddingAngle={2}
                      onClick={(d) => {
                        const targetName = d?.name || d?.payload?.name;
                        if (targetName) handleSelect(targetName);
                      }}
                      onMouseEnter={(d) => {
                        const targetName = d?.name || d?.payload?.name;
                        if (targetName) setHoveredPayerName(targetName);
                      }}
                      cursor="pointer"
                      isAnimationActive={false}
                    >
                      {sorted.map((d) => {
                        const isSelected = currentActivePayer === d.name;
                        const isHovered = hoveredPayerName === d.name;
                        const isOtherSelected =
                          currentActivePayer !== "All" &&
                          currentActivePayer !== d.name;
                        return (
                          <Cell
                            key={d.name}
                            fill={d.color}
                            opacity={isOtherSelected && !isHovered ? 0.3 : 1}
                            stroke={
                              isSelected
                                ? "#0F172A"
                                : isHovered
                                  ? "#ffffff"
                                  : "none"
                            }
                            strokeWidth={isSelected || isHovered ? 2 : 0}
                          />
                        );
                      })}
                    </Pie>
                    <Tooltip
                      content={<CustomTooltip />}
                      wrapperStyle={{ zIndex: 1000 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Col>

            {/* Ranked List of Payers with Progress Bars */}
            <Col xs={12} lg={7}>
              <div className="d-flex flex-column gap-2">
                {sorted.map((p, i) => {
                  const active = currentActivePayer === p.name;
                  const hovered = hoveredPayerName === p.name;
                  return (
                    <button
                      key={p.name}
                      type="button"
                      onClick={() => handleSelect(p.name)}
                      onMouseEnter={() => setHoveredPayerName(p.name)}
                      onMouseLeave={() => setHoveredPayerName(null)}
                      className="w-100 d-flex align-items-center justify-content-between gap-3 px-3 py-2 rounded-3 border text-start transition-all"
                      style={{
                        backgroundColor: active
                          ? "rgba(59, 130, 246, 0.15)"
                          : hovered
                            ? "rgba(148, 163, 184, 0.15)"
                            : "transparent",
                        borderColor: active
                          ? "#3B82F6"
                          : hovered
                            ? "rgba(148, 163, 184, 0.4)"
                            : "rgba(148, 163, 184, 0.2)",
                        boxShadow: active
                          ? "0 0 0 2px rgba(59, 130, 246, 0.2)"
                          : "none",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        className="d-flex align-items-center gap-2"
                        style={{ minWidth: "135px" }}
                      >
                        <span
                          className="fw-bold text-muted font-monospace text-center"
                          style={{ fontSize: "12px", width: "18px" }}
                        >
                          {i + 1}
                        </span>
                        <span
                          className="rounded-circle flex-shrink-0"
                          style={{
                            width: "10px",
                            height: "10px",
                            backgroundColor: p.color,
                          }}
                        />
                        <span
                          className="fw-bold text-truncate"
                          style={{
                            fontSize: "13px",
                            maxWidth: "120px",
                            color: active ? "#3B82F6" : "currentColor",
                          }}
                          title={p.name}
                        >
                          {p.name}
                        </span>
                      </div>

                      {/* Horizontal Percentage Bar */}
                      <div
                        className="flex-grow-1 rounded-pill overflow-hidden d-none d-sm-block"
                        style={{ height: "8px", backgroundColor: "rgba(148, 163, 184, 0.15)" }}
                      >
                        <div
                          className="h-100 rounded-pill"
                          style={{
                            width: `${Math.max(2, p.pct)}%`,
                            backgroundColor: p.color,
                          }}
                        />
                      </div>

                      <div className="d-flex align-items-center gap-3 flex-shrink-0">
                        <span
                          className="fw-bold text-end font-monospace"
                          style={{
                            fontSize: "13px",
                            width: "48px",
                            color: active ? "#3B82F6" : "currentColor",
                          }}
                        >
                          {p.pct}%
                        </span>
                        <span
                          className="text-muted text-end font-monospace"
                          style={{ fontSize: "12px", width: "85px" }}
                        >
                          {metric === "gross"
                            ? fmtSAR(p.gross, true)
                            : `${fmtNum(p.claims)} clm`}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default PayerMarketShareChart;
